import isEmpty from 'lodash/isEmpty';
import { useCallback, useContext, useEffect, useMemo, useState, type JSX } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ServiceCode, IntegrationSystem, UserRequestStatus } from '@/shared/types';

import { Loader } from '@/shared/ui/Loader';
import { validateMultiplePhoneFieldsForERZ } from '@/components/Forms/Inputs/MultiplePhoneFieldForERZ';
import { Form, StepContainer, FormElement } from '@/components/old';

import { config } from '@/config/index';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { SERVICE_CODES } from '@/lib/consts';

import { DocumentData, FormDataType, UserRequest, FormDocument } from '@/api/types';

import api from '@/api';

import {
  ApproveAndDownloadButton,
  HomeButton,
  NextStepButton,
  PreviousStepButton,
  SignButton,
} from '../../../../../../components/Forms/Buttons';
import {
  AdditionalDocumentFieldName,
  attachmentMapping,
  getServiceData,
} from '../../../../../../components/Forms/constants';
import {
  additionalDocumentsFieldNamesForServer,
  additionalDocumentsPayloadGroupMockup,
} from '../../../../../../components/Forms/Steps/AdditionalDocument/utils';
import {
  confirmationOfApplicationDataFieldNamesForServer,
  confirmationOfApplicationDataPayloadGroupMockup,
  ConfirmationOfApplicationFieldName,
} from '../../../../../../components/Forms/Steps/ConfirmationOfApplication';
import {
  payloadRegisteredGroupName as placeOfResidencePayloadRegisteredGroupName,
  payloadDeclaredGroupName as placeOfResidencePayloadDeclaredGroupName,
  placeOfResidenceDeclaredDataFieldNamesForServer,
  placeOfResidenceDeclaredDataPayloadGroupMockup,
  placeOfResidenceRegisteredDataFieldNamesForServer,
  placeOfResidenceRegisteredDataPayloadGroupMockup,
  PlaceOfResidenceDataFieldName,
} from '../../../../../../components/Forms/Steps/PlaceOfResidence';
import AuthContext from '../../../../../../contexts/Auth/AuthContext';
import ModalContext from '../../../../../../contexts/Modal/ModalContext';
import { ModalName } from '../../../../../../contexts/Modal/modalTypes';
import { ServiceAvailabilityProvider } from '../../../../../../contexts/ServiceAvailability/ServiceAvailabilityContext';
import { clearLocationState } from '../../../../../../helpers/clearLocationState';
import { formatDate } from '../../../../../../helpers/dates';
import { findAllDocumentTypesInFormState } from '../../../../../../helpers/findAllDocumentTypesInFormState';
import {
  markEmptyInputFieldWithAnError,
  removeErrorMarkFromInputField,
  validateRequiredFieldsToBeFilled,
} from '../../../../../../helpers/invalidFields';
import { ServiceFileManager } from '../../../../../../helpers/serviceFileManager';
import { useAsyncDataLoader } from '../../../../../../hooks/useAsyncDataLoader';
import { useFormDraft } from '../../../../../../hooks/useFormDraft';
import {
  currentServiceCode,
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  serviceName,
  stepNumberToDownloadPdf,
  stepNumberToSubmitForm,
  totalSteps,
} from '../TemporaryResidenceRelocation';

import Step1 from './Step1';
import Step2, { weaponsDataFieldNamesForServer, weaponsDataPayloadGroupMockup } from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import {
  Step5,
  AddressCheckStatus,
  declaredAddressNewDataFieldNamesForServer,
  declaredAddressNewDataPayloadGroupMockup,
} from './Step5';
import Step6 from './Step6';
import Step7 from './Step7';
import Step8 from './Step8';
import Step9 from './Step9';

interface groups {
  name: string;
}

export interface StepProps {
  formData: FormDataType;
  handleInputChange: (name: string, value: unknown) => void;
}

enum ServerFieldName {
  declaredAddressNew = 'declaredAddressNew',
  personPassportType = 'doc_type',
  gender = 'sex',
  registeredCity = 'settlement',
  registeredStreet = 'street',
  weapons = 'weapons',
}

const fieldNameForServer = {
  ...weaponsDataFieldNamesForServer,

  // OWNER
  Name: {
    name: 'first_name',
    group: 'owner',
  },
  lastName: {
    name: 'last_name',
    group: 'owner',
  },
  Patronymic: {
    name: 'patronymic',
    group: 'owner',
  },
  birthday: {
    name: 'birthday',
    group: 'owner',
  },
  gender: {
    name: ServerFieldName.gender,
    group: 'owner',
  },
  phones: {
    name: 'phones',
    group: 'owner',
  },
  email: {
    name: 'email',
    group: 'owner',
  },
  personPassportType: {
    name: ServerFieldName.personPassportType,
    group: 'owner',
  },
  seriaDocPesron: {
    name: 'serial',
    group: 'owner',
  },
  numberDocPesron: {
    name: 'serial',
    group: 'owner',
  },
  authDepDocPerson: {
    name: 'auth_dep',
    group: 'owner',
  },
  citizenship: {
    name: 'citizenship',
    group: 'owner',
  },
  issued: {
    name: 'issued',
    group: 'owner',
  },
  activeIssuedDate: {
    name: 'issued',
    group: 'owner',
  },
  expired: {
    name: 'expired',
    group: 'owner',
  },
  authDepCode: {
    name: 'auth_dep',
    group: 'owner',
  },
  unzr: {
    name: 'unzr',
    group: 'owner',
  },
  rnokpp: {
    name: 'rnokpp',
    group: 'owner',
  },
  birthPlace: {
    name: 'birth_place',
    group: 'owner',
  },

  // REGISTERED
  ...placeOfResidenceRegisteredDataFieldNamesForServer,

  // DECLARED
  ...placeOfResidenceDeclaredDataFieldNamesForServer,

  ...declaredAddressNewDataFieldNamesForServer,

  // ORG_NPU
  ...confirmationOfApplicationDataFieldNamesForServer,

  // ADDITIONAL_INFO
  ...additionalDocumentsFieldNamesForServer,
};

const noGroupInput: groups[] = [];

const FormData: {
  [key: string]: {
    [key: string]: unknown;
  };
} = {
  ...weaponsDataPayloadGroupMockup,
  owner: {
    last_name: null,
    first_name: null,
    patronymic: null,
    birthday: null,
    phones: null,
    email: null,
    doc_type: null,
    doc_name: null,
    serial: null,
    issued: null,
    expired: null,
    auth_dep: null,
    unzr: null,
    rnokpp: null,
    birth_place: null,
    sex: null,
    citizenship_id: null,
    citizenship_name: null,
  },
  ...placeOfResidenceRegisteredDataPayloadGroupMockup,
  ...placeOfResidenceDeclaredDataPayloadGroupMockup,
  ...declaredAddressNewDataPayloadGroupMockup,
  ...confirmationOfApplicationDataPayloadGroupMockup,
  ...additionalDocumentsPayloadGroupMockup,
  service: {
    id: null,
    name: null,
    code: null,
  },
};

const FormDataAttachments: {
  attachments?: unknown[];
} = {
  attachments: [
    {
      id: null,
      typeDoc: null,
      fileName: null,
      code: null,
      flag: 'default',
      info: {},
      encoded_contents: [],
    },
  ],
};

const FIELDS_WITH_ATTACHMENTS_ARRAY = ['declaredAddressNewDocuments'];

type Props = {
  onReceiveRequestId: (requestId: string) => void;
};

const FormSteps: React.FC<Props> = ({ onReceiveRequestId }) => {
  const modalContext = useContext(ModalContext);
  const { documentsData } = useContext(AuthContext);
  const { renderSpecificModal } = useContext(ModalContext);
  const { getServiceByCode } = useContext(ServicesContext);
  const { state: locationState } = useLocation();
  const { createDraft, saveDraft, loadDraft, deleteDraft, prefillFormData } = useFormDraft();
  const navigate = useNavigate();
  const {
    data: serviceDraft,
    load: loadServiceDraft,
    isLoading: isServiceDraftLoading,
  } = useAsyncDataLoader(() => loadDraft(currentServiceCode as ServiceCode));

  const passportDocument = useMemo<Nullable<DocumentData>>(() => documentsData['passport'], [documentsData]);

  const [disabledCreatePDF, setDisabledCreatePDF] = useState<boolean>(false);
  const [addresCheckStatus, setAddressCheckStatus] = useState<AddressCheckStatus>({
    isError: false,
    addressType: null,
  });
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [currentRequestId, setCurrentRequestId] = useState<UserRequest['id']>('');
  const [isDisabled, setIsDisabled] = useState<boolean>(!config.signUp.testFlow);
  const [data, setData] = useState<FormDataType>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);

  const usedDocumentTypeCodes = useMemo(
    () => findAllDocumentTypesInFormState(data, FIELDS_WITH_ATTACHMENTS_ARRAY),
    [data],
  );

  const isServiceChanged = useMemo<boolean>(
    () => FormData.service.code !== currentServiceCode,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [FormData.service, currentServiceCode],
  );

  const scrollTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const handleRequestIdChange = (requestId: string): void => {
    setCurrentRequestId(requestId);
    onReceiveRequestId(requestId);
  };

  useEffect(() => {
    scrollTop();

    if (isServiceChanged && currentServiceCode) {
      getServiceData(currentServiceCode, getServiceByCode(currentServiceCode))
        .then(({ service, applicationAttachment }) => {
          FormData.service = service;
          if (FormDataAttachments.attachments) {
            FormDataAttachments.attachments[0] = applicationAttachment;
          }
        })
        .catch((error) => {
          if (currentServiceCode && (error.status === 403 || error.status === 401)) {
            getServiceData(currentServiceCode, getServiceByCode(currentServiceCode));
          }
        });
    }
  }, [isServiceChanged, scrollTop]);

  useEffect(() => {
    // Redirect from UserRequests.draft by pressing button "Продовжити"
    if (locationState?.continueDraft && locationState?.request_id) {
      setIsLoading(true);
      handleRequestIdChange(locationState.request_id);

      prefillFormData({
        draftId: locationState.request_id,
        handleInputChange: handleInputChange,
      })
        .then(({ meta, parsedStateForm }) => {
          setData(parsedStateForm);
          scrollTop();
          meta?.current_step && setCurrentStep(meta.current_step);
          return loadServiceDraft();
        })
        .finally(() => setIsLoading(false));
    }

    clearLocationState();
  }, []);

  const handleClearField = useCallback((name: string) => {
    setData((prevFormData) => ({
      ...prevFormData,
      [name]: null,
    }));
  }, []);

  const handleInputChange = (name: string, value: unknown): void => {
    const isFieldInGroup = Object.keys(fieldNameForServer).includes(name);

    if (!isFieldInGroup) {
      if (
        !noGroupInput.some((x) => {
          return x.name === name;
        })
      ) {
        noGroupInput.push({
          name: name,
        });
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (value === null || (value instanceof Date && isNaN(value as any))) {
      setData((prevFormData) => ({
        ...prevFormData,
        [name]: null,
      }));
    } else if (typeof value === 'string' || typeof value === 'boolean') {
      setData((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    } else if (typeof value === 'object') {
      if (value instanceof Array) {
        setData((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      } else {
        const stringValue = JSON.stringify(value).replace(/^"(.*)"$/, '$1');
        setData((prevFormData) => ({
          ...prevFormData,
          [name]: stringValue,
        }));
      }
    }

    removeErrorMarkFromInputField(name);
  };

  const handleNextStep = async (): Promise<void> => {
    if (addresCheckStatus.isError) {
      modalContext.renderSpecificModal({
        modalName: ModalName.AddressCheckAlert,
        modalProps: {
          addressType: addresCheckStatus.addressType,
        },
      });

      return;
    }

    if (currentStep === 1 && currentServiceCode) {
      if (isEmpty(passportDocument)) {
        renderSpecificModal({
          modalName: ModalName.DocumentMissingAlert,
          modalProps: {
            serviceName,
            navigate,
          },
        });

        return;
      }

      try {
        setIsLoading(true);

        const draftId = await createDraft({
          id: FormData.service.id as string,
          name: FormData.service.name as string,
          code: currentServiceCode,
          totalSteps,
        });

        handleRequestIdChange(draftId);

        await loadServiceDraft();
      } catch (error) {
        const parsedError = JSON.parse(error as string);
        const { requestId, reason } = JSON.parse(parsedError.message) as {
          requestId: number;
          reason: UserRequestStatus.DRAFT;
        };

        switch (reason) {
          case UserRequestStatus.DRAFT:
            renderSpecificModal({
              modalName: ModalName.ServiceDraftExistingAlert,
              modalProps: {
                startNewDraft: () => {
                  setIsLoading(true);

                  deleteDraft(`${requestId}`)
                    .then(async () => {
                      setData({});
                      await handleNextStep(); // createDraft logic again
                    })
                    .finally(() => setIsLoading(false));
                },
                continueDraft: () => {
                  setIsLoading(true);

                  prefillFormData({
                    draftId: `${requestId}`,
                    handleInputChange: handleInputChange,
                  })
                    .then(({ meta, parsedStateForm }) => {
                      setData(parsedStateForm);
                      scrollTop();
                      handleRequestIdChange(`${requestId}`);
                      meta?.current_step && setCurrentStep(meta.current_step);
                      return loadServiceDraft();
                    })
                    .finally(() => setIsLoading(false));
                },
              },
            });
            return;

          default:
            console.error(error);
            break;
        }
      } finally {
        setIsLoading(false);
      }

      scrollTop();
      setCurrentStep((prevStep) => prevStep + 1);
      return;
    }

    if (currentStep === 2 && currentServiceCode === SERVICE_CODES.RESIDENCE_RELOCATION) {
      scrollTop();
      setCurrentStep((prevStep) => prevStep + 1);
      return;
    }

    const hasInvalidPhoneFields = currentStep === 3 ? !(await validateMultiplePhoneFieldsForERZ()) : false;
    const { hasBlankFields, blankInputFields } = validateRequiredFieldsToBeFilled(data);
    const hasValidationErrors = hasBlankFields || hasInvalidPhoneFields;

    hasBlankFields && blankInputFields.forEach(markEmptyInputFieldWithAnError);

    if (!hasValidationErrors && currentStep < totalSteps) {
      setIsLoading(true);

      try {
        if (serviceDraft?.id) {
          await saveDraft({
            draftId: serviceDraft.id,
            draftCurrentStep: serviceDraft?.meta?.current_step || 0,
            meta: {
              current_step: currentStep,
              total_steps: totalSteps,
            },
            stateForm: data,
          });

          await loadServiceDraft();

          setCurrentStep((prevStep) => prevStep + 1);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      } finally {
        setIsLoading(false);
        scrollTop();
      }
    }
  };

  const handlePrevStep = (): void => {
    setDisabledCreatePDF(false);
    setAddressCheckStatus({
      isError: false,
      addressType: null,
    });

    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
      scrollTop();
    }
  };

  const StepButtons = function (): JSX.Element {
    const orgNpu = data['orgNpu'];
    const checkbox = data['allowToDataProcessing'];

    let containerBtn = null;

    if (currentStep === 1) {
      containerBtn = <NextStepButton handleNextStep={handleNextStep} />;
    } else if (currentStep > 1 && currentStep < stepNumberToDownloadPdf) {
      containerBtn = (
        <>
          <PreviousStepButton handlePrevStep={handlePrevStep} />
          <NextStepButton handleNextStep={handleNextStep} />
        </>
      );
    } else if (currentStep === stepNumberToDownloadPdf) {
      containerBtn = (
        <>
          <PreviousStepButton handlePrevStep={handlePrevStep} />
          <ApproveAndDownloadButton
            disabled={!orgNpu || !checkbox || disabledCreatePDF}
            onClick={(): void => {
              setIsLoading(true);
              setDisabledCreatePDF(true);
              create_peyload();
            }}
          />
        </>
      );
    } else if (currentStep === stepNumberToSubmitForm) {
      containerBtn = (
        <SignButton
          disabled={isDisabled || isLoading}
          onClick={(): void => {
            setIsLoading(true);
            create_peyload();
          }}
        />
      );
    } else {
      containerBtn = <HomeButton />;
    }

    return !!containerBtn && <FormElement variant="bottom">{containerBtn}</FormElement>;
  };

  const renderFormStep = (step: number): Nullable<JSX.Element> => {
    switch (step) {
      case 1:
        return <Step1 />;

      case 2:
        return <Step2 formData={data} handleInputChange={handleInputChange} />;

      case 3:
        return (
          <Step3 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} isERZ />
        );

      case 4:
        return <Step4 formData={data} handleClearInput={handleClearField} handleInputChange={handleInputChange} />;

      case 5:
        return (
          <Step5
            formData={data}
            setAddressCheckStatus={setAddressCheckStatus}
            handleClearInput={handleClearField}
            handleInputChange={handleInputChange}
          />
        );

      case 6:
        return (
          <Step6
            formData={data}
            handleClearInput={handleClearField}
            handleInputChange={handleInputChange}
            disallowedDocumentTypeCodes={[
              ...usedDocumentTypeCodes,
              ...disallowedDocumentTypeCodesForAdditionalDocumentStep,
            ]}
          />
        );

      case 7:
        return <Step7 formData={data} handleInputChange={handleInputChange} />;

      case 8:
        return (
          <Step8
            fileManager={serviceFileManager}
            rnokpp={data.rnokpp as string}
            onSignComplete={() => setIsDisabled(false)}
          />
        );

      case 9:
        return currentServiceCode ? <Step9 formData={data} serviceCode={currentServiceCode} /> : <></>;

      default:
        return null;
    }
  };

  function create_peyload(): void {
    if (FormDataAttachments.attachments) {
      FormDataAttachments.attachments = [FormDataAttachments.attachments[0]];
    }

    async function createFormDate(): Promise<void> {
      async function createFileBase64(files: File[]): Promise<string[]> {
        const arrFiles: string[] = [];
        if (currentStep !== stepNumberToDownloadPdf) {
          for (let f = 0; f < files.length; f++) {
            const reader = new FileReader();
            const filePromise = new Promise<string>((resolve) => {
              reader.onload = function (): void {
                // eslint-disable-next-line @typescript-eslint/no-shadow
                const base64String = btoa(reader.result as string);
                resolve(base64String.toString());
              };
            });
            reader.readAsBinaryString(files[f]);
            const base64String = await filePromise;
            arrFiles.push(base64String);
          }
        }
        return arrFiles;
      }

      function createInfo(
        info: Record<string, string>,
        // eslint-disable-next-line @typescript-eslint/no-shadow
        data: Record<string, unknown>,
      ): Record<string, unknown> {
        const obj: Record<string, unknown> = {};

        Object.keys(info).forEach((key) => {
          const infoKey = info[key];
          if (data[infoKey]) {
            obj[key] = formatDate(data[infoKey] as string, '-', true);
          }
        });
        return obj;
      }

      function createAdditionalDocumentInfo(info?: Record<string, string>): Record<string, unknown> {
        const obj: Record<string, unknown> = {};

        info &&
          Object.entries(info).forEach(([key, value]) => {
            obj[key] = formatDate(value as string, '-', true);
          });

        return obj;
      }

      for (const i in data) {
        if (Object.hasOwnProperty.call(data, i)) {
          const serverName = (
            fieldNameForServer as {
              [key: string]: { name: string; group: string };
            }
          )[i];

          if (serverName && data[i]) {
            const formDataValue = data[i];
            const jsonString = typeof formDataValue === 'string' ? formDataValue : JSON.stringify(formDataValue);
            const group = serverName.group;
            const name = serverName.name;
            const id_key = name + '_id';
            const name_key = name + '_name';

            if (!group || !name) {
              return;
            }

            let obj;
            try {
              obj = JSON.parse(jsonString);
            } catch (e) {}

            if (obj && typeof obj === 'object') {
              if (obj instanceof Object) {
                if (name === ServerFieldName.declaredAddressNew) {
                  for (const addressFieldName in obj) {
                    if (Object.prototype.hasOwnProperty.call(obj, addressFieldName)) {
                      const addressFieldValue = obj[addressFieldName];

                      if (addressFieldValue instanceof Object) {
                        FormData[group][`${addressFieldName}_id`] = addressFieldValue.id;
                        FormData[group][`${addressFieldName}_name`] = addressFieldValue.label;
                      } else {
                        FormData[group][addressFieldName] = addressFieldValue;
                      }
                    }
                  }
                } else if (name === ServerFieldName.personPassportType) {
                  FormData[group].doc_type = obj.code;
                  FormData[group].doc_name = obj.label;
                } else if (name === ServerFieldName.gender) {
                  FormData[group].sex = obj.id;
                } else {
                  if (FormData[group][id_key] === undefined) {
                    FormData[group].id = obj.id;
                    FormData[group].name = obj.label;
                  } else {
                    FormData[group][id_key] = obj.id;
                    FormData[group][name_key] = obj.label;
                  }
                }
              }
              if (obj instanceof Array) {
                if (name === ServerFieldName.weapons) {
                  FormData[group][name] = [];

                  obj.forEach((weapon: string) => {
                    const { id, weaponType: type } = JSON.parse(weapon);

                    (FormData[group][name] as unknown[]).push({ id, type });
                  });
                } else {
                  FormData[group][name] = obj;
                }
              }
            } else {
              if (name === ServerFieldName.registeredCity || name === ServerFieldName.registeredStreet) {
                FormData[group][id_key] = null;
                FormData[group][name_key] = data[i];
              } else {
                FormData[group][name] = formatDate(data[i]?.toString() ?? '', '-', true);
              }
            }
          } else {
            const statusGroup = noGroupInput.find((x) => {
              return x.name === i;
            });
            if (statusGroup) {
              let obj;
              try {
                obj = JSON.parse(data[i]?.toString() ?? '');
              } catch (e) {}

              if (i === 'declaredAddressNewDocuments' && FormDataAttachments.attachments) {
                for (const document of data[i] as unknown as Array<FormDocument<IntegrationSystem.ERZ>>) {
                  const { type, attachments, info = {} } = document;

                  FormDataAttachments.attachments.push({
                    id: type?.id,
                    typeDoc: type?.label,
                    fileName: type?.label,
                    code: type?.code,
                    addressType: obj?.addressType,
                    info,
                    flag: 'default',
                    encoded_contents: Array.isArray(attachments) ? await createFileBase64(attachments) : [],
                  });
                }
              }

              if (i === AdditionalDocumentFieldName.additionalDocuments && FormDataAttachments.attachments) {
                for (const additionalAttachment of data[i] as unknown as FormDocument[]) {
                  const { type, attachments, info } = additionalAttachment;

                  FormDataAttachments.attachments.push({
                    id: type?.id,
                    typeDoc: type?.label,
                    fileName: type?.label,
                    code: type?.code,
                    info: createAdditionalDocumentInfo(info),
                    flag: 'additional',
                    encoded_contents: Array.isArray(attachments) ? await createFileBase64(attachments) : [],
                  });
                }
              }

              if (attachmentMapping[i] && obj && FormDataAttachments.attachments) {
                const infoAttach = attachmentMapping[i].info;
                const infoContents = attachmentMapping[i].encoded_contents as string;

                FormDataAttachments.attachments.push({
                  id: obj.id,
                  typeDoc: obj.label,
                  fileName: obj.label,
                  code: obj.code,
                  flag: 'default',
                  info: createInfo(infoAttach, data),
                  encoded_contents: Array.isArray(data[infoContents])
                    ? await createFileBase64(data[infoContents] as File[])
                    : [],
                });
              }
            }
          }
        }
      }

      if (!data[PlaceOfResidenceDataFieldName.registeredDoc] === true) {
        FormData[placeOfResidencePayloadDeclaredGroupName] = {
          ...FormData[placeOfResidencePayloadRegisteredGroupName],
        };
      }
    }

    async function someFunction(): Promise<void> {
      await createFormDate();
      await requestFormData();
    }

    someFunction();
    scrollTop();
  }

  async function requestFormData(): Promise<void> {
    if (currentStep === stepNumberToDownloadPdf && FormDataAttachments.attachments && currentServiceCode) {
      const FormDataAttachmentsWithoutApplication = {
        attachments: FormDataAttachments.attachments.slice(1),
      };

      api.services
        .generatePdf(currentServiceCode, {
          requestId: currentRequestId,
          ...FormData,
          ...FormDataAttachmentsWithoutApplication,
        })
        .then((response) => {
          serviceFileManager.setAttachment(response);
          setCurrentStep((prevStep) => prevStep + 1);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }

    if (currentStep === stepNumberToSubmitForm && currentServiceCode) {
      if (FormDataAttachments.attachments) {
        (FormDataAttachments.attachments[0] as any).encoded_contents = await serviceFileManager.getBase64Array();
      }

      api.services
        .submit(
          currentServiceCode,
          {
            requestId: currentRequestId,
            ...FormData,
            ...FormDataAttachments,
            ...serviceFileManager.getSignature(),
          },
          String(data[ConfirmationOfApplicationFieldName.allowToProfileUpdate]),
        )
        .then(() => {
          serviceFileManager.download();
          serviceFileManager.clear();
          setCurrentStep((prevStep) => prevStep + 1);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        })
        .finally(() => setIsLoading(false));
    }
  }

  return (
    <Form name="residenceRelocation" className="max-width-md" isBgDefault={true}>
      <StepContainer isLoading={isLoading || isServiceDraftLoading} data-step={currentStep}>
        {currentServiceCode ? (
          <ServiceAvailabilityProvider
            serviceCode={currentServiceCode}
            skipAvailabilityValidation={currentStep === 1 || currentStep === totalSteps}
          >
            {isLoading || isServiceDraftLoading ? (
              <Loader centered />
            ) : (
              <>
                {renderFormStep(currentStep)}
                {StepButtons()}
              </>
            )}
          </ServiceAvailabilityProvider>
        ) : null}
      </StepContainer>
    </Form>
  );
};

export default FormSteps;
